import type { KaraokeStore } from './KaraokeStore';
import type { KaraokeData } from '@src/editor/types/karaoke';

/**
 * PLAT-10816 — the caption-language half of an undo/redo snapshot, shared by
 * `VideoCreatorStore` and `ClipVideoCreatorAdapter` so the two cannot drift.
 *
 * Restoring a snapshot rehydrates karaoke through
 * `KaraokeStore.hydrateLoadedByLanguage`, which picks a variant by preferring the
 * **current** `activeLanguage` and otherwise falling back to the source key. A
 * snapshot that does not say which language was active therefore cannot be
 * restored correctly once an undo spans a language change: switch ES, switch FR,
 * undo, and the preference is `fr-FR` — absent from the snapshot's map — so it
 * lands on the source variant instead of the Spanish one it captured.
 */
export interface KaraokeLanguageSnapshot {
  /**
   * The language of the track actually loaded in the editor, raw so the
   * `'original'` sentinel survives — that is the form DS8's load preference
   * reads.
   */
  karaokeActiveLanguage?: string;
  /**
   * Captured eagerly for the same reason `karaokeData` is: the surrounding save
   * is async, so `currentVideo`'s copy goes stale and can be missing the very
   * variant the restored language needs.
   */
  karaokeDataByLanguage?: Record<string, KaraokeData>;
}

/**
 * Reads the config's language, NOT `activeLanguage`. A switch sets
 * `activeLanguage` to its target *before* running the commands
 * (`switchCaptionLanguage`), and the snapshot is taken before the first of them,
 * so `activeLanguage` already names the language being switched TO while the
 * snapshot must describe the state being switched FROM. The config only moves
 * when `UpdateKaraokeConfigCommand` executes, which is after the snapshot — the
 * same "the key must describe the payload" rule `liveKey` follows for the
 * per-language write-back. Falls back to `activeLanguage` before the first sync,
 * when there is no config yet.
 */
export function captureKaraokeLanguageState(
  karaokeStore: KaraokeStore,
): KaraokeLanguageSnapshot {
  return {
    karaokeActiveLanguage:
      karaokeStore.defaultKaraokeConfig?.language ??
      karaokeStore.activeLanguage,
    karaokeDataByLanguage: karaokeStore.getAllKaraokeDataByLanguage(),
  };
}

/**
 * The language the store actually ended up on, in the form a UI selection uses
 * (`null` = Original). Read this AFTER hydration rather than replaying what the
 * snapshot asked for: hydration falls back to the source variant when the
 * requested language has no entry, and a UI left pointing at the requested
 * language would then disagree with the captions on screen.
 *
 * Compares through `activeKey`, the store's own keying chokepoint, rather than
 * reading `activeLanguage` directly: that field holds a RAW value in general
 * (the `'original'` sentinel, or `en-us` where `sourceLanguageKey` is the
 * normalized `en-US`), and a raw-vs-normalized mismatch would return the source
 * language as if it were a translation. `activeKey` collapses the sentinel and
 * canonicalizes, so both sides of the comparison are in the same vocabulary.
 *
 * That makes this the only place a *canonical* code enters a UI selection, which
 * elsewhere carries raw codes (`?clipLang=`, the toggle's own options). Safe
 * because those options come from the showcase's `supportedLanguages` — a linked
 * DatoCMS Language catalogue whose `value` is already canonical BCP-47
 * (`en-US`, `pt-BR`), so `normalizeLangKey` is the identity on them.
 */
export function resolveRestoredUiLanguage(
  karaokeStore: KaraokeStore,
): string | null {
  const key = karaokeStore.activeKey;
  return key === karaokeStore.sourceLanguageKey ? null : key;
}

/**
 * Point the store at the snapshot's language before hydration reads it. No-op
 * when the field is absent, so a normal load still lets the `?lang=` seeding in
 * `RootLayout` own the choice.
 *
 * @returns the language it seeded, or `null` when it did nothing — callers use
 * this as the signal to mirror the restored language into their own UI
 * selection (with {@link resolveRestoredUiLanguage}, not this return value).
 */
export function seedKaraokeLanguageFromSnapshot(
  karaokeStore: KaraokeStore,
  snapshot: KaraokeLanguageSnapshot | null | undefined,
): string | null {
  const lang = snapshot?.karaokeActiveLanguage;
  if (!lang) return null;
  karaokeStore.setActiveLanguage(lang);
  return lang;
}
